import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CreateEntityChangeRequestDocument,
  type CreateEntityChangeRequestMutation,
  type CreateEntityChangeRequestMutationVariables,
} from "~/features/entities/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface ActivationTarget {
  id: string;
  recordMetadataId: string;
}

/**
 * The change request input plus the relations an activation asks to bring back.
 *
 * Written here rather than taken from the generated input type because the field ships with
 * this work unit's backend change: Dewey's generated types pick it up the next time codegen
 * runs against a Hasura that has the deployed remote schema. Delete this once they have.
 */
type ChangeRequestCreateInputWithCascade = CreateEntityChangeRequestMutationVariables["input"] & {
  cascadeRelationIds?: string[];
};

/**
 * Activate / deactivate an entity. In Cnut this is not a direct mutation — it
 * creates a Change Request that flips `deactivatedAt`. The hook returns plain
 * submitters + a `processing` flag; the calling container owns the confirm dialog.
 */
export function useEntityActivation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [createChangeRequest] = useMutation<
    CreateEntityChangeRequestMutation,
    CreateEntityChangeRequestMutationVariables
  >(CreateEntityChangeRequestDocument);

  const submit = useCallback(
    async (target: ActivationTarget, deactivate: boolean, cascadeRelationIds: string[] = []) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        const input: ChangeRequestCreateInputWithCascade = {
          tenantId,
          entityId: target.id,
          recordMetadataId: target.recordMetadataId,
          modelPropsInput: { deactivatedAt: deactivate ? new Date().toISOString() : null },
          description: "",
          context: { action: deactivate ? "EntityDeactivateButton" : "EntityActivateButton" },
          // Deactivation cascades on its own; activation brings back only what was ticked.
          ...(deactivate || cascadeRelationIds.length === 0 ? {} : { cascadeRelationIds }),
        };
        await createChangeRequest({ variables: { input } });
        toast.success(
          deactivate
            ? t("feature.entities.detail.requestedDeactivateSuccessfully")
            : t("feature.entities.detail.requestedActivateSuccessfully"),
        );
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [createChangeRequest, tenantId, t],
    // tenantId guarded inside
  );

  const onActivate = useCallback(
    (target: ActivationTarget, cascadeRelationIds: string[] = []) => submit(target, false, cascadeRelationIds),
    [submit],
  );
  const onDeactivate = useCallback((target: ActivationTarget) => submit(target, true), [submit]);

  return { onActivate, onDeactivate, processing };
}
